/**
 * Admin Dashboard Overview.
 * Displays dynamic, real-time database metric summaries and recent activity.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  TrendingUp,
  ShoppingBag,
  Users,
  IndianRupee,
  Package,
  Plus,
  ShieldAlert,
  Inbox,
} from "lucide-react";
import { useCurrentUser } from "@/hooks/use-auth";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { LoadingSkeleton } from "@/components/common/loading-skeleton";
import {
  fetchAdminProductsFromSupabase,
  type ExtendedProduct,
} from "@/features/admin/products/store/admin-products-store";
import {
  fetchAdminOrdersFromSupabase,
  type AdminOrderRecord,
} from "@/features/admin/orders/store/admin-orders-store";
import {
  fetchAdminCustomersFromSupabase,
  type CustomerRecord,
} from "@/features/admin/customers/store/admin-customers-store";

import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: `Admin Dashboard — ${siteConfig.name}` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDashboard,
});

function AdminDashboard() {
  const { isAuthenticated, isAdmin, isLoading, user } = useCurrentUser();
  const [productsList, setProductsList] = useState<ExtendedProduct[]>([]);
  const [ordersList, setOrdersList] = useState<AdminOrderRecord[]>([]);
  const [customersList, setCustomersList] = useState<CustomerRecord[]>([]);

  const refreshMetrics = () => {
    fetchAdminProductsFromSupabase().then((prods) => {
      if (prods && prods.length > 0) setProductsList(prods);
    });
    fetchAdminOrdersFromSupabase().then((ords) => {
      if (ords && ords.length > 0) setOrdersList(ords);
    });
    fetchAdminCustomersFromSupabase().then((custs) => {
      if (custs && custs.length > 0) setCustomersList(custs);
    });
  };

  useEffect(() => {
    // 2. Fetch live data from Supabase
    refreshMetrics();

    const channelName = `admin-metrics-live-sync-${Math.random().toString(36).substring(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () =>
        refreshMetrics(),
      )
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, () =>
        refreshMetrics(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="section-container py-32 text-center">
        <LoadingSkeleton className="h-64 w-full max-w-3xl mx-auto" />
      </div>
    );
  }

  // 1. Unauthenticated users -> Redirect to login
  if (!isAuthenticated) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <ShieldAlert className="mx-auto h-12 w-12 text-amber-500" />
        <h1 className="mt-4 font-display text-2xl">Authentication Required</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Please sign in to access the Enterprise Console.
        </p>
        <Link
          to="/account/login"
          className="mt-6 inline-flex min-h-11 items-center bg-primary px-6 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
        >
          Sign In
        </Link>
      </div>
    );
  }

  // 2. Authenticated non-admin users -> 403 Forbidden View
  if (!isAdmin) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <p className="eyebrow mt-6 text-destructive">403 Forbidden</p>
        <h1 className="mt-2 font-display text-3xl">Access Denied</h1>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Your account (<strong className="text-foreground">{user?.email}</strong>) does not have
          administrator privileges.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/account"
            className="inline-flex min-h-11 items-center border border-border px-6 text-xs uppercase tracking-wider hover:bg-secondary"
          >
            Return to My Account
          </Link>
        </div>
      </div>
    );
  }

  // Calculate real metric aggregations from actual uploaded data
  const totalRevenue = ordersList.reduce((acc, o) => acc + o.total, 0);
  const totalOrders = ordersList.length;
  const totalCustomers = customersList.length;
  const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;

  const stats = [
    {
      title: "Total Revenue",
      value: formatPrice(totalRevenue),
      change: totalOrders > 0 ? "Real-time sync" : "No sales recorded yet",
      icon: IndianRupee,
      path: "/admin/orders",
    },
    {
      title: "Total Orders",
      value: totalOrders.toString(),
      change: totalOrders > 0 ? "Real-time sync" : "No orders yet",
      icon: ShoppingBag,
      path: "/admin/orders",
    },
    {
      title: "Active Customers",
      value: totalCustomers.toString(),
      change: totalCustomers > 0 ? "Registered users" : "No users yet",
      icon: Users,
      path: "/admin/customers",
    },
    {
      title: "Avg Order Value",
      value: formatPrice(avgOrderValue),
      change: "Calculated",
      icon: TrendingUp,
      path: "/admin/orders",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs uppercase tracking-wider font-semibold text-primary">
            Enterprise Console
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-foreground font-bold">
            Dashboard Overview
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/products"
            className="inline-flex min-h-11 items-center gap-2 bg-primary px-6 text-xs tracking-[0.16em] uppercase text-primary-foreground font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-xs"
          >
            <Plus className="h-4 w-4" /> Add Product
          </Link>
          <Link
            to="/admin/settings"
            className="inline-flex min-h-11 items-center border border-border px-6 text-xs tracking-[0.16em] uppercase font-semibold text-foreground rounded-lg hover:bg-secondary transition-colors"
          >
            Settings
          </Link>
        </div>
      </div>

      {/* Metric Cards - All Clickable */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Link
            key={stat.title}
            to={stat.path as never}
            className="bg-card border border-border/90 p-6 rounded-2xl shadow-xs hover:shadow-md hover:border-primary/40 transition-[color,background-color,border-color,box-shadow,transform,opacity] block group"
          >
            <div className="flex items-center justify-between">
              <span className="eyebrow text-xs uppercase tracking-wider font-semibold text-muted-foreground group-hover:text-primary transition-colors">
                {stat.title}
              </span>
              <div className="h-9 w-9 rounded-xl bg-secondary/60 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                <stat.icon className="h-4 w-4" strokeWidth={1.5} />
              </div>
            </div>
            <p className="mt-4 font-display text-2xl sm:text-3xl tabular-nums font-bold text-foreground">
              {stat.value}
            </p>
            <p className="mt-2 text-xs text-muted-foreground font-medium flex items-center justify-between">
              <span>{stat.change}</span>
              <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                View →
              </span>
            </p>
          </Link>
        ))}
      </div>

      {/* Main Grid: Recent Orders & Top Products */}
      <div className="grid gap-8 lg:grid-cols-12">
        {/* Recent Orders Card */}
        <div className="lg:col-span-8 bg-card border border-border/90 rounded-2xl p-6 shadow-xs overflow-hidden">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-2.5">
              <ShoppingBag className="h-5 w-5 text-primary" />
              <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                Recent Orders
              </h2>
            </div>
            <Link
              to="/admin/orders"
              className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
            >
              View all orders →
            </Link>
          </div>
          {ordersList.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Inbox className="mx-auto h-10 w-10 text-muted-foreground/60" strokeWidth={1} />
              <p className="mt-3 text-xs font-semibold text-foreground">No orders recorded yet</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Real customer purchases will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="border-b border-border uppercase text-[11px] font-bold text-muted-foreground tracking-wider bg-secondary/20">
                  <tr>
                    <th className="py-3 px-3.5 rounded-l-lg">Order</th>
                    <th className="py-3 px-3.5">Customer</th>
                    <th className="py-3 px-3.5">Total</th>
                    <th className="py-3 px-3.5">Status</th>
                    <th className="py-3 px-3.5 text-right rounded-r-lg">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {ordersList.slice(0, 5).map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => {
                        window.location.href = "/admin/orders";
                      }}
                      className="hover:bg-secondary/30 transition-colors cursor-pointer group"
                    >
                      <td className="py-4 px-3.5 font-mono font-bold text-foreground group-hover:text-primary">
                        {order.id}
                      </td>
                      <td className="py-4 px-3.5">
                        <p className="font-bold text-foreground">{order.customerName}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{order.email}</p>
                      </td>
                      <td className="py-4 px-3.5 tabular-nums font-bold text-foreground">
                        {formatPrice(order.total)}
                      </td>
                      <td className="py-4 px-3.5">
                        <span className="inline-block rounded-full bg-emerald-100 dark:bg-emerald-950 px-2.5 py-1 text-[10px] font-bold text-emerald-800 dark:text-emerald-300">
                          {order.status}
                        </span>
                      </td>
                      <td className="py-4 px-3.5 text-right">
                        <Link
                          to="/admin/orders"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Products Card */}
        <div className="lg:col-span-4 bg-card border border-border/90 rounded-2xl p-6 shadow-xs overflow-hidden">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-2.5">
              <Package className="h-5 w-5 text-primary" />
              <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                Top Products
              </h2>
            </div>
            <Link
              to="/admin/products"
              className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
            >
              View catalog →
            </Link>
          </div>
          {productsList.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Package className="mx-auto h-10 w-10 text-muted-foreground/60" strokeWidth={1} />
              <p className="mt-3 text-xs font-semibold text-foreground">No products uploaded yet</p>
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-border/60">
              {productsList.slice(0, 4).map((p) => (
                <li key={p.id}>
                  <Link
                    to="/admin/products"
                    className="py-3.5 px-2 flex items-center gap-3.5 hover:bg-secondary/30 rounded-xl transition-colors group cursor-pointer"
                  >
                    <img
                      src={p.image}
                      alt={p.name}
                      className="h-12 w-12 object-contain bg-secondary/30 border border-border/80 rounded-xl p-1 shrink-0 group-hover:scale-105 transition-transform"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.src = "/placeholder.svg";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate text-foreground group-hover:text-primary transition-colors">
                        {p.name}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{p.category}</p>
                    </div>
                    <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-200/50 dark:border-emerald-900/50 tabular-nums">
                      {formatPrice(p.price)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
